'use es6';

import Immutable from 'immutable';
import * as ScopeGroup from 'ecosystem-marketplace-core/constants/scopes/ScopeGroup';
import DataSync from 'ecosystem-marketplace-core/records/DataSync';
import Pricing from 'marketplace-ui-common/records/Pricing';
import Screenshot from 'ecosystem-marketplace-core/records/Screenshot';
import IntegrationFeature from 'ecosystem-marketplace-core/records/IntegrationFeature';
import SocialScreenshots from 'ecosystem-marketplace-core/records/SocialScreenshots';
import { cleanObject } from 'ecosystem-marketplace-core/utils/formatters';
import { getCategorySuperCategory } from 'ecosystem-marketplace-core/utils/groupTags';
import AppReviewability from './AppReviewability';
import Scope from 'developer-experience-shared-components/scopes/models/Scope';
import ThinOffering from './ThinOffering';
const EMPTY_LIST = Immutable.List();
const EMPTY_SET = Immutable.Set();
const scopeGroupValues = Immutable.Set(Object.values(ScopeGroup));

const buildScopeGroupsSet = scopeGroups => Immutable.Set(scopeGroups).filter(scopeGroup => scopeGroupValues.includes(scopeGroup));

const defaults = {
  agencyPricingUrl: null,
  appIntegrations: EMPTY_SET,
  appLanguages: EMPTY_SET,
  appUrl: '',
  benefits: '',
  casesUrl: null,
  category: null,
  categoryFeatures: EMPTY_SET,
  chatUrl: null,
  companyName: '',
  companyUrl: null,
  currency: 'USD',
  dataSyncHowItWorks: null,
  dataSyncInfo: null,
  dataSyncs: EMPTY_LIST,
  email: '',
  facebookUrl: null,
  forumUrl: null,
  hubSpotFeatures: EMPTY_SET,
  iconUrl: '',
  id: 0,
  integrationFeatures: EMPTY_LIST,
  keywords: EMPTY_SET,
  language: null,
  name: '',
  neededScopeGroups: EMPTY_LIST,
  offering: new ThinOffering(),
  offeringId: 0,
  opsHub: false,
  phone: null,
  pricingUrl: null,
  pricings: EMPTY_LIST,
  privacy2Url: null,
  privacy3Url: null,
  privacyUrl: '',
  queueDepth: null,
  requiredRelevantScopes: EMPTY_SET,
  reviewNotes: null,
  reviewability: new AppReviewability(),
  screenshots: EMPTY_LIST,
  setupUrl: '',
  socialScreenshots: new SocialScreenshots(),
  supportLanguages: EMPTY_SET,
  supportUrl: null,
  tagline: '',
  termsUrl: '',
  tiers: EMPTY_SET,
  updatedAt: null,
  videoUrl: null
};

class ListingView extends Immutable.Record(defaults) {
  constructor(json = {}) {
    const {
      appIntegrations,
      appLanguages,
      categoryFeatures,
      dataSyncs,
      hubSpotFeatures,
      integrationFeatures,
      keywords,
      neededScopeGroups,
      offering,
      pricings,
      requiredRelevantScopes,
      reviewability,
      screenshots,
      socialScreenshots,
      supportLanguages,
      tiers
    } = json;

    if (dataSyncs) {
      json.dataSyncs = Immutable.List(dataSyncs).map(item => new DataSync(item));
    }

    if (offering) {
      json.offering = new ThinOffering(offering);
    }

    if (appIntegrations) {
      json.appIntegrations = Immutable.Set(appIntegrations);
    }

    if (appLanguages) {
      json.appLanguages = Immutable.Set(appLanguages);
    }

    if (categoryFeatures) {
      json.categoryFeatures = Immutable.Set(categoryFeatures);
    }

    if (hubSpotFeatures) {
      json.hubSpotFeatures = Immutable.Set(hubSpotFeatures);
    }

    if (keywords) {
      json.keywords = Immutable.Set(keywords);
    }

    if (supportLanguages) {
      json.supportLanguages = Immutable.Set(supportLanguages);
    }

    if (tiers) {
      json.tiers = Immutable.Set(tiers);
    }

    if (reviewability) {
      json.reviewability = new AppReviewability(reviewability);
    } else {
      json.reviewability = new AppReviewability({
        isPublic: true
      });
    }

    if (pricings) {
      json.pricings = Immutable.List(pricings).map(item => new Pricing(item));
    }

    if (requiredRelevantScopes) {
      json.requiredRelevantScopes = buildScopeGroupsSet(requiredRelevantScopes);
    }

    if (neededScopeGroups) {
      json.neededScopeGroups = Immutable.List(neededScopeGroups.map(item => new Scope(item)));
    }

    if (screenshots) {
      json.screenshots = Immutable.List(screenshots).map(item => new Screenshot(item));
    }

    if (socialScreenshots) {
      json.socialScreenshots = new SocialScreenshots(socialScreenshots);
    }

    if (integrationFeatures) {
      json.integrationFeatures = Immutable.List(integrationFeatures).map(item => new IntegrationFeature(item));
    }

    super(cleanObject(json));
  }

  getSuperCategory() {
    return getCategorySuperCategory(this.category);
  }

  getSourceId() {
    return this.getIn(['offering', 'sourceId']);
  }

  getAppId() {
    const sourceId = this.getSourceId();
    return sourceId ? parseInt(sourceId, 10) : null;
  }

  getIsCertified() {
    return !!this.offering.certifiedAt;
  }

  getEventProperties() {
    const applicationId = this.getAppId();
    return {
      applicationId,
      applicationName: this.name,
      category: this.getSuperCategory(),
      certification: this.getIsCertified(),
      subcategory: this.category,
      contentLanguage: this.language,
      // hasExternalConnectFlow: null,
      homemade: this.getIn(['offering', 'homemade']),
      offeringId: this.offeringId
    };
  }

  setReviewability(reviewability) {
    return this.set('reviewability', new AppReviewability(reviewability));
  }

}

export default ListingView;